import React, { useState, useEffect } from "react";
import {
  getRequest,
  postRequest,
  patchRequest,
} from "../../../axiosRoutes/index";
import {
  Button,
  Modal,
  Box,
  Typography,
  OutlinedInput,
  FormControl,
  InputLabel,
  Grid,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useSnackbar } from "../../../axiosRoutes/snackbarConfig";
import dayjs from "dayjs";

const currentYear = dayjs().year();
const minDate = `${currentYear}-01-01`;
const maxDate = `${currentYear+2}-12-31`;

const AddFitness = ({ open, handleClose, fitnessId }) => {
  const { snackbar, showSnackbar, handleSnackbarClose } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [fitnessData, setFitnessData] = useState({
    userId: "",
    date: "",
    status: "",
    comments: "",
    totalAmount: "",
    image: "",
  });

  const fetchStatus = async () => {
    try {
      const response = await getRequest("filter/status");
      const statusArray = Object.keys(response).map((key) => ({
        label: key,
        value: response[key],
      }));
      setStatuses(statusArray);
    } catch (error) {
      console.error("Error fetching Filter:", error);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    const fetchFitness = async () => {
      if (fitnessId !== null) {
        const access = localStorage.getItem("access");
        try {
          setLoading(true);
          const response = await getRequest(
            `admin/fitnessAllowance/${fitnessId}`,
            {
              headers: {
                Authorization: `Bearer ${access}`,
              },
            }
          );
          const formatDate = new Date(response.date)
            .toISOString()
            .split("T")[0];
          setFitnessData({
            userId: response.userId,
            date: formatDate,
            status: response.status,
            comments: response.comments,
            totalAmount: response.totalAmount,
            image: response.imageUrl || "",
          });
        } catch (error) {
          console.error("Error fetching Fitness Allowances:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setFitnessData({
          userId: "",
          date: "",
          status: "",
          comments: "",
          totalAmount: "",
          image: "",
        });
      }
    };
    fetchFitness();
  }, [fitnessId]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      userId: fitnessData.userId,
      date: fitnessData.date,
      status: fitnessData.status,
      comments: fitnessData.comments,
      totalAmount: fitnessData.totalAmount,
    };
    if (fitnessData.image) {
      if (typeof fitnessData.image === "string") {
        payload.image = "";
      } else {
        const file = fitnessData.image;
        const base64Image = await toBase64(file);
        payload.image = base64Image;
      }
    }

    try {
      const access = localStorage.getItem("access");
      await patchRequest(
        `admin/fitnessAllowance/${fitnessId}`,
        {
          Authorization: `Bearer ${access}`,
          "Content-Type": "application/json",
        },
        JSON.stringify(payload)
      );
      showSnackbar("Fitness Updated Successfully", "success");
      setFitnessData({
        userId: "",
        date: "",
        status: "",
        comments: "",
        totalAmount: "",
        image: "",
      });
      handleClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      const access = localStorage.getItem("access");
      try {
        const response = await getRequest("employee/getAllEmployees", {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        });
        const activeEmployees = response.filter(emp => emp.status === "active");
        setEmployees(activeEmployees);
      } catch (error) {
        console.error("Error fetching Employees:", error);
      }
    };
    fetchEmployees();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFitnessData({ ...fitnessData, [name]: value });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFitnessData({ ...fitnessData, image: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      userId: fitnessData.userId,
      date: fitnessData.date,
      status: fitnessData.status,
      comments: fitnessData.comments,
      totalAmount: fitnessData.totalAmount,
    };
    if (fitnessData.image) {
      const file = fitnessData.image;
      const base64Image = await toBase64(file);
      payload.image = base64Image;
    }
    try {
      const access = localStorage.getItem("access");
      await postRequest(
        "admin/fitnessAllowance",
        {
          Authorization: `Bearer ${access}`,
          "Content-Type": "application/json",
        },
        JSON.stringify(payload)
      );
      showSnackbar("Fitness Added Successfully", "success");
      handleClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });


  return (
    <>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "70%", md: "35%" },
            maxHeight: "80vh",
            overflowY: "auto",
            bgcolor: "black",
            color: "#FEFEFE",
            border: "1px solid #A9A9A9",
            p: 2,
            "& .MuiOutlinedInput-root": {
              color: "#FEFEFE",
              "& fieldset": {
                borderColor: "#FEFEFE",
              },
              "&:hover fieldset": {
                borderColor: "#FEFEFE",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FEFEFE",
              },
            },
            "& .MuiInputLabel-root": {
              color: "#FEFEFE",
            },
            "& .MuiIconButton-root": {
              color: "#FEFEFE",
            },
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            component="h1"
            sx={{ mb: 2 }}
          >
            {fitnessId ? "Update Fitness Allowance" : "Add Fitness Allowance"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel id="employee-select-label">Employee</InputLabel>
                <Select
                  labelId="employee-select-label"
                  name="userId"
                  readOnly={fitnessId ? true : false}
                  value={fitnessData.userId}
                  onChange={handleChange}
                  label="Employee"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                      },
                    },
                  }}
                >
                  {employees ? (employees.map((employee) => (
                    <MenuItem
                      key={employee.userId._id}
                      value={employee.userId._id}
                    >
                      {employee.userId.name}
                    </MenuItem>
                  ))) : <MenuItem disabled={true}>No Data yet</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel
                  htmlFor="outlined-adornment-date"
                  shrink
                  sx={{
                    backgroundColor: "#000",
                    paddingX: "4px",
                    marginLeft: "-4px",
                  }}
                >
                  Date
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-date"
                  type="date"
                  name="date"
                  label="Date"
                  value={fitnessData.date}
                  onChange={handleChange}
                  inputProps={{ min: minDate, max: maxDate }}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-totalAmount">
                  Total Amount
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-totalAmount"
                  type="text"
                  name="totalAmount"
                  label="Total Amount"
                  value={fitnessData.totalAmount}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-status">
                  Status
                </InputLabel>
                <Select
                  labelId="outlined-adornment-status-label"
                  id="outlined-adornment-status"
                  name="status"
                  label="Status"
                  value={fitnessData.status}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                >
                  {statuses.map((statusOpt) => (
                    <MenuItem key={statusOpt.value} value={statusOpt.value}>
                      {statusOpt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-Comments">
                  Comments
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-comments"
                  type="text"
                  name="comments"
                  label="Comments"
                  value={fitnessData.comments}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
                <Button
                  component="span"
                  size="small"
                  variant="contained"
                  sx={{
                    width: "auto",
                    bgcolor: "#E5622D",
                    "&:hover": { bgcolor: "#D0521B" },
                  }}
                >
                  {fitnessData.image ? "Update Attachment" : "Add Attachment"}
                </Button>
              </label>
            </Grid>
            {fitnessData.image && (
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                {typeof fitnessData.image === "string" ? (
                  <img
                    src={fitnessData.image}
                    alt="Preview"
                    style={{ maxWidth: "25%", height: "auto" }}
                  />
                ) : (
                  <img
                    src={URL.createObjectURL(fitnessData.image)}
                    alt="Preview"
                    style={{ maxWidth: "25%", height: "auto" }}
                  />
                )}
              </Grid>
            )}
            {fitnessId !== null ? (
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleUpdate}
                  sx={{
                    bgcolor: "#E5622D",
                    "&:hover": { bgcolor: "#D0521B" },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                  ) : (
                    "Update"
                  )}
                </Button>
              </Grid>
            ) : (
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  sx={{
                    bgcolor: "#E5622D",
                    "&:hover": { bgcolor: "#D0521B" },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                  ) : (
                    "Add"
                  )}
                </Button>
              </Grid>
            )}
            <Grid item xs={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleClose}
                disabled={loading}
                sx={{ bgcolor: "#A9A9A9", "&:hover": { bgcolor: "#E1E1E1" } }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddFitness;
